import { useState, type ComponentType, type FC, type MouseEvent } from 'react'

import { ExpandableDescription, StyledText, Tooltip } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import ArrowLeftIcon from 'mdi-react/ArrowLeftIcon'
import HeadphonesIcon from 'mdi-react/HeadphonesIcon'

import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import { PolicyProductCard } from '@/components/policy/ProductCard'
import { normalizeProductData } from '@/components/policy/ProductCard/helper'
import type { ProductMoreInfoProps } from '@/components/policy/ProductCard/ProductInfoPopup'
import { ProductMoreInfoOverview } from '@/components/policy/ProductCard/ProductInfoPopup'
import {
  usePolicyEditAccess,
  usePolicyId,
  useUpdateProduct,
} from '@/modules/ecom-policy/policy/hooks'
import { usePolicyFilters } from '@/modules/ecom-policy/policy/Policy.context'
import type { ProductExtended } from '@/modules/ecom-policy/types'

import { handleMultiProductSelect } from './helpers'

export const ProductGroupOverview: FC<{
  description: string | null
  groupName: string
  groupedProducts: ProductExtended[]
  groupKey: string
  hasAccessories: boolean
  isMandatory: boolean
  onModalClose: () => void
}> = ({
  description,
  groupedProducts,
  groupName,
  groupKey,
  hasAccessories,
  isMandatory,
  onModalClose,
}) => {
  const translate = useTranslation()
  const { updateProduct } = useUpdateProduct()
  const policyId = usePolicyId()
  const hasEditAccess = usePolicyEditAccess()

  const filters = usePolicyFilters()
  const { setProductsWithVisibleAccessoriesIds } = filters
  const [, setCurrentlyBrowsedGroupKey] = filters.currentlyBrowsedGroupKey
  const [isNextPage, setIsNextPage] = useState(false)
  const [moreInfoProduct, setMoreInfoProduct] = useState<ProductMoreInfoProps | null>(null)

  const handleOnlySelectedProductAccessoriesClick = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation()

    const selectedGroupedProductsIds = groupedProducts.map(({ id }) => id)
    setProductsWithVisibleAccessoriesIds(selectedGroupedProductsIds)
    setCurrentlyBrowsedGroupKey(groupKey)
  }

  if (isNextPage && moreInfoProduct != null) {
    return (
      <div className="pb-4">
        <div className="mb-4 text-left">
          <Button
            size="small"
            variant="tertiary"
            className="mx-0 px-0"
            Icon={<ArrowLeftIcon />}
            onClick={() => setIsNextPage(false)}
          >
            <Translation id="Go Back" />
          </Button>
        </div>
        <ProductMoreInfoOverview
          {...moreInfoProduct}
          isGroupedProduct={true}
          onClose={() => setIsNextPage(false)}
        />
      </div>
    )
  }
  const isAllSelected = groupedProducts.every(product => product.isSelected)

  return (
    <div className="mobile:m-4 m-0 flex flex-col justify-center gap-4">
      <StyledText size="large" fontWeight="bold" colorMode="black">
        {groupName}
      </StyledText>

      <ExpandableDescription
        description={description || ''}
        showFullText={false}
        TranslationComponent={Translation as ComponentType}
      />
      <div className="flex flex-col gap-4 sm:flex-row">
        <span
          data-tooltip-id={`remove-product-tooltip-${groupKey}`}
          data-tooltip-content={translate(
            `A mandatory product cannot be removed. To enable removing, change the "Is mandatory" option and save`
          )}
        >
          <Button
            className="mobile:w-fit m-0 w-full"
            variant={isAllSelected ? 'danger' : 'primary'}
            disabled={!hasEditAccess || (isAllSelected && isMandatory)}
            onClick={e => {
              e.preventDefault()
              if (!hasEditAccess) {
                return
              }
              handleMultiProductSelect({
                updateProduct,
                groupedProducts,
                isAllSelected,
                policyId,
              })
            }}
          >
            {isAllSelected ? <Translation id="Remove all" /> : <Translation id="Select All" />}
          </Button>
        </span>
        {isAllSelected && isMandatory && (
          <Tooltip id={`remove-product-tooltip-${groupKey}`} style={{ zIndex: 100 }} size="large" />
        )}

        {hasAccessories && (
          <Button
            variant="primary"
            className="mobile:w-fit m-0 w-full"
            Icon={<HeadphonesIcon />}
            onClick={handleOnlySelectedProductAccessoriesClick}
          >
            <Translation id="See accessories" />
          </Button>
        )}
      </div>

      <div className="grid-cols-fill-10 mobile:grid-cols-fill-15 mt-2 grid justify-center gap-4">
        {groupedProducts.map(product => {
          const { id } = product
          const normalizedData = normalizeProductData({ singleProduct: product, hasAccessories })
          return (
            <PolicyProductCard
              key={id}
              {...normalizedData}
              showNextPage={() => setIsNextPage(true)}
              setMoreInfoProduct={setMoreInfoProduct}
            />
          )
        })}
      </div>

      <footer className="text-center">
        <Button variant="tertiary" className="min-w-32" onClick={onModalClose}>
          <Translation id="Close" />
        </Button>
      </footer>
    </div>
  )
}
